/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { TFnType } from '@tolgee/react'
import {
  DetectionBoundingBox,
  RemoveBoundingBoxFromAnnotatedImageMutation,
  UpdateBoundingBoxMutation,
} from '@/graphql'

export type DetectionClass = 'PRODUCT' | 'PRICE_TAG' | 'EMPTY_FACING'

export type DetectionTagOption = {
  id: string
  name: string
}

export type DetectionBox = Pick<
  DetectionBoundingBox,
  'id' | 'minX' | 'maxX' | 'minY' | 'maxY' | 'sourceImageUrl'
> & {
  detectionClass: DetectionClass
  tagId: string | null
  tag: string | null
}

export const DETECTION_CLASS_ORDER: DetectionClass[] = [
  'PRODUCT',
  'PRICE_TAG',
  'EMPTY_FACING',
]

export const getDetectionClassLabels = (
  t: TFnType,
): Record<DetectionClass, string> => ({
  PRODUCT: t('detection.classes.product', 'Product'),
  PRICE_TAG: t('detection.classes.priceTag', 'Price Tag'),
  EMPTY_FACING: t('detection.classes.emptyFacing', 'Empty Facing'),
})

export const DEFAULT_PRODUCT_TAG = 'beverage'

export const getOperationErrorMessage = (
  t: TFnType,
  messages?: { message: string }[],
) =>
  messages?.[0]?.message ||
  t('common.somethingWentWrong', 'Something went wrong.')

export const isSyntheticTagId = (tagId: string) => tagId.startsWith('tag-')

export const normalizeTagName = (name: string) =>
  name.trim().toLowerCase().replace(/\s+/g, ' ')

export const mergeTagOptions = (tags: DetectionTagOption[]) =>
  Array.from(
    new Map(tags.map(tag => [normalizeTagName(tag.name), tag])).values(),
  ).sort((left, right) => left.name.localeCompare(right.name))

export const toDetectionTagId = (tagName: string) =>
  `tag-${tagName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')}`

export const INITIAL_DETECTION_TAGS: DetectionTagOption[] = [
  {
    id: toDetectionTagId(DEFAULT_PRODUCT_TAG),
    name: DEFAULT_PRODUCT_TAG,
  },
]

export const getDetectionClass = (
  box: Pick<DetectionBoundingBox, 'metaClass'>,
): DetectionClass => {
  if (box.metaClass === 'PRICE_TAG') return 'PRICE_TAG'
  if (box.metaClass === 'EMPTY_FACING') return 'EMPTY_FACING'
  return 'PRODUCT'
}

export const toDetectionBox = (
  box: Pick<
    DetectionBoundingBox,
    | 'id'
    | 'sourceImageUrl'
    | 'minX'
    | 'maxX'
    | 'minY'
    | 'maxY'
    | 'metaClass'
    | 'tagId'
    | 'tag'
  >,
): DetectionBox => ({
  id: box.id,
  sourceImageUrl: box.sourceImageUrl,
  minX: box.minX,
  maxX: box.maxX,
  minY: box.minY,
  maxY: box.maxY,
  detectionClass: getDetectionClass(box),
  tagId: box.tagId ?? null,
  tag: box.tag ?? null,
})

const BEVERAGE_TAGS = new Set(['beverage'])

export const toMetaClass = (
  detectionClass: DetectionClass,
  tagName?: string | null,
): DetectionBoundingBox['metaClass'] => {
  if (detectionClass === 'PRICE_TAG') return 'PRICE_TAG'
  if (detectionClass === 'EMPTY_FACING') return 'EMPTY_FACING'
  return tagName && BEVERAGE_TAGS.has(normalizeTagName(tagName))
    ? 'BEVERAGE'
    : 'NON_BEVERAGE_PRODUCT'
}

export const getTagColor = (tag: string) => {
  let hash = 0
  for (let index = 0; index < tag.length; index += 1) {
    hash = (hash * 31 + tag.charCodeAt(index)) >>> 0
  }

  const hue = hash % 360
  return `hsl(${hue}, 72%, 52%)`
}

export const getDetectionClassColor = (
  detectionClass: DetectionClass,
  activeTag: string,
) => {
  if (detectionClass === 'PRICE_TAG') return '#dc2626'
  if (detectionClass === 'EMPTY_FACING') return '#d97706'
  return getTagColor(activeTag)
}

export const isUpdateBoundingBoxResult = (
  payload: UpdateBoundingBoxMutation['updateBoundingBox'] | undefined,
): payload is Extract<
  UpdateBoundingBoxMutation['updateBoundingBox'],
  { __typename: 'UpdateBoundingBoxResult' }
> => payload?.__typename === 'UpdateBoundingBoxResult'

export const isRemoveBoundingBoxPayload = (
  payload:
    | RemoveBoundingBoxFromAnnotatedImageMutation['removeBoundingBoxFromAnnotatedImage']
    | undefined,
): payload is Extract<
  RemoveBoundingBoxFromAnnotatedImageMutation['removeBoundingBoxFromAnnotatedImage'],
  { __typename: 'RemoveBoundingBoxResult' }
> => payload?.__typename === 'RemoveBoundingBoxResult'
